<template lang="pug">
div
  v-card.round-xl.fill-height(outlined)
    v-row(no-gutters)
      //- profile
      v-col.pa-4
        v-row(dense)
          v-col.shrink
            v-img.round-xl(v-if="patient.profile_image" width="60" height="80" :src="patient.profile_image.file" cover)
              template(v-slot:placeholder)
                v-row(class="fill-height ma-0" align="center" justify="center")
                  v-progress-circular(indeterminate color="grey")
            v-card.neutral300.fill-height.round-xl(v-else flat width="60" height="80")
              div.d-flex.align-center.justify-center.fill-height
                v-icon(size="30") $yzi-user-03
          v-col
            div.text-sm.font-medium.neutral700--text {{ patient.name }}
            div.text-xs.neutral500--text(v-if="patient.dob") {{ $options.filters.getAgeByYearMonthDay(patient.dob) | displayAgeByYearMonthDay }} old
            div.text-xs.neutral500--text {{ patient.ic }}
            div.text-xs.neutral500--text(v-if="patient.gender") {{ patient.gender }}
          v-col.shrink(align="right")
            y-button(label="View profile" outlined color="neutral400" text-color="neutral700" size="xxs" height="24" unset-min-width button-class="px-2" @click="openProfileWindow")
            div.text-xs.neutral500--text.no-wrap.mt-2(v-if="registration") Queue No: {{ registration.queue_no }}
      v-divider.neutral300(vertical)
      //- payment method
      v-col.pa-4
        div.d-flex.align-center
          div.text-xs.font-medium.neutral700--text Payment method
          v-spacer
          y-button.mr-1(v-if="hasPanelLink" label="Verify" outlined color="neutral400" text-color="neutral700" unset-min-width size="xxs" height="24" button-class="px-2" @click="openPanelLink()")
          y-button(label="Edit" outlined color="neutral400" text-color="neutral700" unset-min-width size="xxs" height="24" button-class="px-2" :disabled="!registration" @click="showEditPaymentDialog = true")
        //- panel
        div.mt-1(v-if="registration && registration.default_payment.panel")
          y-chip(:label="registration.default_payment.panel.name" size="xs" text-color="neutral500" outlined)
          //- for remark type
          template(v-if="isPanelRemarkType")
            v-tooltip(v-if="registration.default_payment.panel.remarks" bottom color="black" content-class="black tooltip-bottom-arrow")
              template(#activator="{ on, attrs }")
                div.text-xs.neutral500--text.ellipsis-text-1.cursor-help.mt-2(v-bind="attrs" v-on="on") {{ registration.default_payment.panel.remarks }}
              div(style="max-width: 30vw")
                .text-xs {{ registration.default_payment.panel.remarks }}
            div(v-if="hasPaymentNote")
              .text-xxs.neutral700--text.font-semibold.mt-2 Registration notes:
              v-tooltip(bottom color="black" content-class="black tooltip-bottom-arrow")
                template(#activator="{ on, attrs }")
                  div.text-xs.neutral500--text.ellipsis-text-1.cursor-help.mt-2(v-bind="attrs" v-on="on") {{ registration.default_payment.note }}
                div(style="max-width: 30vw")
                  .text-xs {{ registration.default_payment.note }}
          //- for url type
          template(v-else)
            .yz-medium-body-2xs.mt-2 Panel note:
            v-tooltip(v-if="hasPaymentNote" bottom color="black" content-class="black tooltip-bottom-arrow")
              template(#activator="{ on, attrs }")
                div.text-xs.neutral500--text.ellipsis-text-1.cursor-help.mt-1(v-bind="attrs" v-on="on") {{ registration.default_payment.note }}
              div(style="max-width: 30vw")
                .text-xs {{ registration.default_payment.note }}
        //- self-pay
        y-chip(v-else label="Self-pay" text-color="neutral500" size="xs" outlined)

    v-divider.neutral300
    v-row(no-gutters)
      //- underlying conditions
      v-col.pt-3.pb-4.px-4(cols="6")
        div.text-xs.font-medium.neutral700--text(style="height: 24px; align-content: center;") Underlying conditions
        template(v-if="!consultation.underlying_conditions || !consultation.underlying_conditions.length")
          div.text-xxs.italic.neutral500--text No underlying conditions
        yz-truncated-list.mt-1(v-else :items="consultation.underlying_conditions" :gap="8")
          template(#visible-item="{ item }")
            v-tooltip(top color="black" attach content-class="black tooltip-top-arrow")
              template(#activator="{ on, attrs }")
                div.clickable(v-bind="attrs" v-on="on")
                  y-chip(:label="item.name" :width="140" outlined)
                    template(#text)
                      span.ellipsis-text-1
                        span.text-xxs.font-medium.neutral700--text {{ item.name }}
                        span.text-xxs.italic.neutral500--text.ml-1 {{ item.remarks }}
              div(style="max-width: 30vw")
                div.text-xs.font-medium {{ item.name }}
                div.text-xxs.italic {{ item.remarks }}
          template(#hidden-item-tooltip="{ item }")
            li
              div.text-xs.font-medium {{ item.name }}
              div.text-xxs.italic {{ item.remarks }}

      //- allergies
      v-col.pt-3.pb-4.px-4(cols="6")
        div.d-flex.align-center(style="height: 24px")
          div.text-xs.font-medium.neutral700--text Allergies
          v-spacer
          y-button(label="Edit" outlined color="neutral400" text-color="neutral700" unset-min-width size="xxs" height="24" button-class="px-2" @click="showAllergiesConditionDialog = true")
        template(v-if="!consultation.allergies || !consultation.allergies.length")
          div.text-xxs.italic.neutral500--text No allergies
        yz-truncated-list.mt-1(v-else :items="consultation.allergies" :gap="8")
          template(#visible-item="{ item }")
            v-tooltip(top color="black" attach content-class="black tooltip-top-arrow")
              template(#activator="{ on, attrs }")
                div.clickable(v-bind="attrs" v-on="on")
                  y-chip(:label="item.name" :width="140" outlined)
                    template(#text)
                      span.ellipsis-text-1
                        span.text-xxs.font-medium.neutral700--text {{ item.name }}
                        span.text-xxs.italic.neutral500--text.ml-1 {{ item.reactions }}
              div(style="max-width: 30vw")
                div.text-xs.font-medium {{ item.name }}
                div.text-xxs.italic {{ item.reactions }}
          template(#hidden-item-tooltip="{ item }")
            li
              div.text-xs.font-medium {{ item.name }}
              div.text-xxs.italic {{ item.reactions }}
  
  .dialogs
    edit-payment-dialog(v-if="showEditPaymentDialog" :open.sync="showEditPaymentDialog" :pid="consultation.pid" :payment="registration.default_payment" :patient="patient" @updated="onUpdatePayment")
    allergies-conditions-dialog(v-if="showAllergiesConditionDialog" :open.sync="showAllergiesConditionDialog" :patientId="patient && patient.id" :allergies="consultation.allergies" :underlying-conditions="consultation.underlying_conditions" @updated="onUpdateAllergies")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import EditPaymentDialog from '@/components/clinic/dialogs/EditPaymentDialog.vue'
import AllergiesConditionsDialog from '@/components/clinic/consultation/AllergiesConditionsDialog.vue'

export default {
  components: { EditPaymentDialog, AllergiesConditionsDialog },
  props: {
    open: {
      type: Boolean,
    }
  },
  data () {
    return {
      showEditPaymentDialog: false,
      showAllergiesConditionDialog: false,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      patient: 'treatment/getPatient',
      registration: 'treatment/getRegistration',
      consultation: 'treatment/getConsultation',
      isHq: 'hq/getIsHq'
    }),
    hasPanelLink () {
      if (this.registration?.default_payment?.panel?.verification_link) {
        return true
      }
      return false
    },
    hasPaymentNote () {
      if (this.registration?.default_payment?.note !== '') {
        return true
      }
      return false
    },
    isPanelRemarkType () {
      if (this.registration?.default_payment?.type === 'panel') {
        return this.registration.default_payment.panel.display_remarks
      }
      return false
    },
  },
  watch: {
  },
  mounted () {
  },
  methods: {
    ...mapActions(['showMessage']),
    onUpdatePayment(payment) {
      this.$store.commit('treatment/updateRegistration', { default_payment: payment })
    },
    onUpdateAllergies(data) {
      this.$store.commit('treatment/updateConsultation', {
        allergies: data.allergies,
        underlying_conditions: data.underlying_conditions
      })
    },
    openProfileWindow() {
      const link = this.isHq ? `/admin/hq/patient/${this.patient.ic}` : `/admin/clinic/patient/${this.patient.id}`
      window.open(link, '_blank')
    },
    openPanelLink () {
      window.open(this.registration.default_payment.panel.verification_link, '_blank')
    },
  },
}
</script>

<style lang="scss" scoped>
</style>